import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { motion } from 'motion/react';
import { type FC, useState } from 'react';
import { useLocation } from 'react-router';
import { AnimatedArrow } from '../AnimatedArrow';
import { type Sections, Sidebar, type Props as SidebarProps, type Tabs } from '../Sidebar';

interface Props extends SidebarProps {
  tabs: Tabs;
  sections: Sections;
}

export const ButtonRouter: FC<Props> = ({ tabs, sections }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const location = useLocation();

  // Tab paths are relative NavLink targets, and four of them are splat routes whose pages
  // add segments of their own: the last segment naming a tab is the page.
  const currentTab = location.pathname
    .split('/')
    .reverse()
    .map((segment) => tabs.find((tab) => tab.path === segment))
    .find((tab) => tab != null);

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!isOpen) return;
    e.stopPropagation();
    setIsOpen(false);
  };

  return (
    <>
      <Button onClick={() => setIsOpen(!isOpen)}>
        {currentTab?.icon}
        <Label>{currentTab?.title ?? 'Pages'}</Label>
        <AnimatedArrow isDown={isOpen} showClosedToLeft />
      </Button>

      <Overlay
        initial={false}
        animate={{ opacity: isOpen ? 0.5 : 0 }}
        style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
        onClick={(e) => handleOverlayClick(e)}
        onTouchStart={(e) => handleOverlayClick(e)}
      />
      <SidebarContainer
        initial={false}
        animate={{
          height: isOpen ? 250 : 0,
          borderColor: isOpen ? RacemapColors.PaleBlue : '#e7e7e7',
          borderWidth: isOpen ? 2 : 0,
        }}
        onAnimationComplete={() => setCollapsed(!isOpen)}
        style={{
          pointerEvents: isOpen ? 'auto' : 'none',
          // `pointer-events` alone leaves the nav links keyboard-reachable. Hiding waits
          // for the collapse to finish so the content does not vanish mid-animation.
          visibility: !isOpen && collapsed ? 'hidden' : 'visible',
        }}
      >
        <Sidebar tabs={tabs} sections={sections} onTabChoosing={() => setIsOpen(false)} />
      </SidebarContainer>
    </>
  );
};

const Button = styled.button`
  width: 100%;
  height: 100%;
  background: transparent;
  border: unset;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 500;
  color: ${RacemapColors.PaleBlue};

  /* the arrow stays right of any label */
  & > *:last-child {
    margin-left: auto;
  }

  :hover {
    background: ${RacemapColors.CloudBlue};
  }
`;

const Label = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SidebarContainer = styled(motion.div)`
  position: fixed;
  left: 0;
  background: white;
  right: 0;
  height: 0;
  overflow: auto;
  border-top: solid 2px ${RacemapColors.PaleBlue};
  border-bottom: solid 2px ${RacemapColors.PaleBlue};
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 142px;
  left: 0;
  right: 0;
  bottom: 0;
  background: black;
  opacity: 0;
`;
